/**
 * React Query hooks for MCP connections and bindings (M2.7).
 */
import { useAuth } from "@clerk/clerk-react";
import {
  type UseMutationResult,
  type UseQueryResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type {
  CreateMcpBindingRequest,
  CreateMcpInstanceRequest,
  McpBindingsListResponse,
  McpInstanceResponse,
  McpInstancesListResponse,
  McpServersListResponse,
} from "@simpliwork/api-schema";
import type { ApiError } from "@/lib/api-client";
import {
  createMcpBinding,
  createMcpInstance,
  deleteMcpBinding,
  deleteMcpInstance,
  fetchMcpBindings,
  fetchMcpInstances,
  fetchMcpServers,
} from "@/lib/api-client";

export const mcpKeys = {
  servers: () => ["mcp", "servers"] as const,
  instances: () => ["mcp", "instances"] as const,
  bindings: (agentId: string, version: number) => ["mcp", "bindings", agentId, version] as const,
} as const;

// ─── Servers ──────────────────────────────────────────────────────────────────

export function useMcpServers(): UseQueryResult<McpServersListResponse, ApiError | Error> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: mcpKeys.servers(),
    queryFn: () => fetchMcpServers({ getToken }),
  });
}

// ─── Instances ────────────────────────────────────────────────────────────────

export function useMcpInstances(): UseQueryResult<McpInstancesListResponse, ApiError | Error> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: mcpKeys.instances(),
    queryFn: () => fetchMcpInstances({ getToken }),
  });
}

export function useCreateMcpInstance(): UseMutationResult<
  McpInstanceResponse,
  ApiError | Error,
  CreateMcpInstanceRequest
> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createMcpInstance(body, { getToken }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mcpKeys.instances() }),
  });
}

export function useDeleteMcpInstance(): UseMutationResult<void, ApiError | Error, string> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteMcpInstance(id, { getToken }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: mcpKeys.instances() }),
  });
}

// ─── Bindings ─────────────────────────────────────────────────────────────────

export function useMcpBindings(
  agentId: string,
  agentVersion: number,
): UseQueryResult<McpBindingsListResponse, ApiError | Error> {
  const { getToken } = useAuth();
  return useQuery({
    queryKey: mcpKeys.bindings(agentId, agentVersion),
    queryFn: () => fetchMcpBindings(agentId, agentVersion, { getToken }),
    enabled: !!agentId && agentVersion > 0,
  });
}

export function useCreateMcpBinding(): UseMutationResult<
  import("@simpliwork/api-schema").McpBindingResponse,
  ApiError | Error,
  CreateMcpBindingRequest
> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => createMcpBinding(body, { getToken }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: mcpKeys.bindings(variables.agent_id, variables.agent_version),
      });
    },
  });
}

export function useDeleteMcpBinding(
  agentId: string,
  agentVersion: number,
): UseMutationResult<void, ApiError | Error, string> {
  const { getToken } = useAuth();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => deleteMcpBinding(id, { getToken }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: mcpKeys.bindings(agentId, agentVersion) });
    },
  });
}
